import React from 'react'
import EditIcon from '@material-ui/icons/Edit'
import { AuthContainer } from 'containers'
import { Modal, PrimaryButton, QueryStatusAlert, TextBox } from 'components'
import { useSetAvailabilityTarget, useSetDepotInboundTarget } from 'hooks'

type SetSupplierTargetProps = {
  reportName: 'Availability' | 'Depot Inbound'
  targetValue: number | null
}

export const SetSupplierTarget = (props: SetSupplierTargetProps) => {
  const { reportName, targetValue } = props

  const { user, userType } = AuthContainer.useContainer()
  const [open, setOpen] = React.useState(false)
  const [localTargetValue, setLocalTargetValue] = React.useState(targetValue)

  const {
    getSetAvailabilityTargetStatus,
    getSetAvailabilityTargetError,
    mutateSetAvailabilityTarget,
    resetSetAvailabilityTarget,
  } = useSetAvailabilityTarget()

  const {
    getSetDepotInboundTargetStatus,
    getSetDepotInboundTargetError,
    mutateSetDepotInboundTarget,
    resetSetDepotInboundTarget,
  } = useSetDepotInboundTarget()

  const isOkayEnabled = !!localTargetValue && localTargetValue !== targetValue

  const handleOpen = () => {
    setLocalTargetValue(targetValue)
    setOpen(true)
  }

  const handleClose = () => {
    if (reportName === 'Availability') resetSetAvailabilityTarget()
    if (reportName === 'Depot Inbound') resetSetDepotInboundTarget()
    setOpen(false)
  }

  const handleSubmit = () => {
    if (reportName === 'Availability')
      mutateSetAvailabilityTarget(
        {
          availableTarget: localTargetValue,
          updatedBy: user?.morrisonMailId ?? '',
        },
        { onSuccess: handleClose }
      )
    if (reportName === 'Depot Inbound')
      mutateSetDepotInboundTarget(
        {
          depotInboundTarget: localTargetValue,
          updatedBy: user?.morrisonMailId ?? '',
        },
        { onSuccess: handleClose }
      )
  }

  return (
    <>
      <div className="flex flex-row gap-3 items-center" data-testid="supplier-target">
        <span className="p-2 text-lg bg-gray-100 rounded-sm">
          {reportName} % Target <span className="font-semibold">{targetValue ?? '-'}%</span>
        </span>
        {userType === 'Site Administrator' && (
          <EditIcon
            className="cursor-pointer"
            style={{
              fontSize: 44,
              color: 'white',
              backgroundColor: '#00893F',
              padding: 10,
              borderRadius: 4,
            }}
            onClick={handleOpen}
          />
        )}
      </div>
      {open && (
        <Modal title="Configure" maxWidth="xs" open={open} onClose={handleClose}>
          {reportName === 'Availability' && (
            <QueryStatusAlert
              queryStatus={getSetAvailabilityTargetStatus}
              errorTitle="Could not set new Availability target:"
              errorObj={getSetAvailabilityTargetError}
              show404Error
            />
          )}
          {reportName === 'Depot Inbound' && (
            <QueryStatusAlert
              queryStatus={getSetDepotInboundTargetStatus}
              errorTitle="Could not set new Depot Inbound target:"
              errorObj={getSetDepotInboundTargetError}
              show404Error
            />
          )}
          <div className="flex flex-row w-64 gap-2 mb-10">
            <TextBox
              id="select-target"
              label={`${reportName} % Target *`}
              outlined
              value={localTargetValue?.toString() ?? ''}
              onChange={(value) => {
                const numberValue = parseInt(value)
                const isNumber = /^\d+$/.test(value) && !isNaN(numberValue)

                if (!value || (isNumber && numberValue >= 1 && numberValue <= 100)) {
                  setLocalTargetValue(value ? numberValue : null)
                }
              }}
            />
            <span className="flex mt-14 text-xl font-semibold">%</span>
          </div>
          <div className="flex flex-row justify-center gap-6 mb-4">
            <PrimaryButton buttonText="Cancel" outlined onClick={handleClose} />
            <PrimaryButton buttonText="Ok" disabled={!isOkayEnabled} onClick={handleSubmit} />
          </div>
        </Modal>
      )}
    </>
  )
}
